import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  EPropertyType,
  type IProductProperty,
  type IPropertiesProductSave,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Form, Input, Row, Space, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { productPropertiesSave } from '../../reducers/product/reducer';
import { loadProductProperties } from '../../reducers/properties/reducer';
import { SPACING } from '../../theme/tokens';
import Link from '../link';

const { Text, Paragraph, Title } = Typography;

interface IForm {
  productProperties: Record<string, IPropertiesProductSave>;
}

function ProductProperties(): React.ReactElement {
  const dispatch = useAppDispatch();
  const productUUID = useSelector((s: RootState) => s.Product.uuid);
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);
  const productUpdatedAt = useSelector((s: RootState) => s.Product.updatedAt);
  const propertiesProduct = useSelector((s: RootState) => s.Properties.product);
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const [form] = Form.useForm<IForm>();

  const [initialValues, setInitialValues] = useState<IForm>({
    productProperties: {},
  });
  const [formElements, setFormElements] = useState<React.ReactElement[]>([]);

  useEffect((): void => {
    dispatch(loadProductProperties(productUUID));
  }, [productUUID, dispatch]);

  useEffect((): void => {
    const productProperties: Record<string, IPropertiesProductSave> = {};

    propertiesProduct.properties?.forEach(
      (productProperty: IProductProperty): void => {
        productProperties[productProperty.code] = {
          value: productProperty.value,
          maxValue: productProperty.maxValue,
        };
      },
    );

    setInitialValues({
      productProperties,
    });
  }, [propertiesProduct.properties]);

  useEffect((): void => {
    const formElementsUE: React.ReactElement[] =
      propertiesProduct.properties?.map(
        (property: IProductProperty): React.ReactElement => {
          const { code, names, type, propertyValue, unit } = property;

          const propertyLocalizedName: string | null = getTranslation(
            names,
            languageType,
          );

          const propertyName: string = propertyLocalizedName || `** ${code} **`;

          const propertyNameEl: React.ReactElement = (
            <Col span={24}>
              <Space size={SPACING.lg} align="start">
                <Text>{propertyName}</Text>
                {unit && <Text type="secondary">{unit}</Text>}
                <Link to={`/property/${code}`} openInNewTab />
              </Space>
            </Col>
          );

          if (type === EPropertyType.ENUM && propertyValue) {
            const propertyValueName: string | null = getTranslation(
              propertyValue.names,
              languageType,
            );

            return (
              <Row gutter={SPACING.lg} key={code}>
                {propertyNameEl}

                <Col span={24}>
                  <Paragraph>
                    {propertyValueName || 'N/A'}{' '}
                    <small>({propertyValue.code})</small>
                  </Paragraph>
                </Col>
              </Row>
            );
          }

          return (
            <Row gutter={SPACING.lg} key={code}>
              {propertyNameEl}

              <Col xs={24} sm={12}>
                <Form.Item name={['productProperties', code, 'value']}>
                  <Input placeholder="Value" disabled={productInProcess} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name={['productProperties', code, 'maxValue']}>
                  <Input
                    placeholder="Maximal value"
                    disabled={productInProcess}
                  />
                </Form.Item>
              </Col>
            </Row>
          );
        },
      ) || [];

    setFormElements(formElementsUE);
  }, [languageType?.code, propertiesProduct.properties, productUpdatedAt]);

  const onFinish = useCallback(
    ({ productProperties }: IForm): void => {
      dispatch(productPropertiesSave(productUUID, productProperties));
    },
    [dispatch, productUUID],
  );

  return (
    <>
      <Title level={4} style={{ margin: 0 }}>
        Properties
      </Title>

      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        initialValues={initialValues}
      >
        {formElements}

        <Row>
          <Col span={24}>
            <Button
              type="primary"
              htmlType="submit"
              disabled={productInProcess}
            >
              Submit
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default React.memo(ProductProperties);
